import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaReceipt, FaPaperPlane } from 'react-icons/fa';
import 'react-confirm-alert/src/react-confirm-alert.css';
import BotaoFaturamento        from './BotaoFaturamento';
import BotaoEmailContabilidade from './BotaoEmailContabilidade';

import { UserContext } from '../../UserContext';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';
import { usePreventDoubleClick } from '../hooks/usePreventDoubleClick';
import Pagination from '../pagination/Pagination';
import styles from './ListaFaturamento.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

export default function ListaFaturamento() {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const { get } = useHttpClient();
    const { exibirErro } = useNotificacoes();

    // estados
    const [usinas, setUsinas] = useState([]);
    const [clientes, setClientes] = useState([]);

    const [filtros, setFiltros] = useState({
        idUsina: '',
        idCliente: '',   
        mes: new Date().getMonth() + 1,
        ano: new Date().getFullYear(),
    });

    const [lista, setLista]     = useState([]);
    const [total, setTotal]     = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const initialized = useRef(false);

    // 1) redireciona se não logado
    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    // busca usinas apenas uma vez
    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const resp = await get(
                    `/hello-sol/usinas/buscar?idEmpresa=${user.empresa.id}`
                );
                setUsinas(Array.isArray(resp) ? resp : resp.content ?? []);
            } catch (err) {
                exibirErro('Erro ao buscar usinas: ' + err.message);
            }
        })();
    }, [user]);

    /* -------------------- carrega clientes ao escolher usina ---------- */
    useEffect(() => {
        if (!filtros.idUsina) { setClientes([]); return; }
        (async () => {
            try {
                const data = await get(`/hello-sol/usuarios/buscarUsuarioUsina?idUsina=${filtros.idUsina}&idTipoUsuario=3`);
                setClientes(data ?? []);
            } catch {
                exibirErro('Erro ao buscar clientes.');
            }
        })();
    }, [filtros.idUsina]);


    /* -------------------- busca faturamento -------------------- */
    const fetchFaturamento = useCallback(async (newPage = 0) => {
        setLoading(true);
        try {
            const qs = new URLSearchParams({
                idUsina  : filtros.idUsina,
                idCliente: filtros.idCliente,
                mes      : filtros.mes,
                ano      : filtros.ano,
                page     : newPage,
                size     : 10
            });
            const resp = await get(`/hello-sol/faturamentos-usina?${qs}`);

            setLista(resp.content ?? []);
            setTotal(resp.content?.[0]?.totalFaturamento ?? 0);
            setTotalPages(resp.totalPages ?? 0);
            setPage(newPage);
            initialized.current = true;
        } catch (err) {
            exibirErro('Erro ao buscar faturamento: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [get, filtros, exibirErro]);

    /* evita duplo-clique */
    const safeFetch = usePreventDoubleClick(() => fetchFaturamento(0))[0];

    /* -------------------- handlers -------------------- */
    const handleSubmit = e => { e.preventDefault(); safeFetch(); };

    const handleChange = e => {
        const { name, value } = e.target;
        setFiltros(f => {
            const novo = { ...f, [name]: value };
            if (name === 'idUsina') novo.idCliente = '';
            return novo;
        });
    };

    const handlePageChange = p => {
        if (p >= 0 && p < totalPages) fetchFaturamento(p);
    };

    const getMonthName = m =>
        new Date(0, m - 1).toLocaleString('pt-BR', { month: 'long' })
            .replace(/^\w/, c => c.toUpperCase());

    /* ---------- gera array de IDs para o back ---------- */
    const obterIdsSelecionados = () =>
        filtros.idCliente            // cliente único
            ? lista.filter(l => String(l.idCliente) === filtros.idCliente)
                .map(l => l.idConsumo)
            : lista.map(l => l.idConsumo);      // todos da usina



    return (
        <div className={styles_global.container}>
            <h2>Consultar Faturamento</h2>

            <form onSubmit={handleSubmit} className={styles.filterForm}>
                {/* Usina */}
                <div className={`${styles_content.formField} ${styles.usinaField}`}>
                    <label>Usina</label>
                    <select name="idUsina" value={filtros.idUsina} onChange={handleChange} required>
                        <option value="">Selecionar Usina</option>
                        {usinas.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                    </select>
                </div>

                {/* Mês */}
                <div className={`${styles_content.formField} ${styles.mesField}`}>
                    <label>Mês</label>
                    <select name="mes" value={filtros.mes} onChange={handleChange}>
                        {[...Array(12)].map((_, i) =>
                            <option key={i + 1} value={i + 1}>{getMonthName(i + 1)}</option>)}
                    </select>
                </div>

                {/* Ano */}
                <div className={`${styles_content.formField} ${styles.anoField}`}>
                    <label>Ano</label>
                    <input
                        type="number"
                        name="ano"
                        value={filtros.ano}
                        onChange={handleChange}
                        min="2020"
                    />
                </div>

                {/* Cliente */}
                <div className={`${styles_content.formField} ${styles.clienteField}`}>
                    <label>Cliente</label>
                    <select
                        name="idCliente"
                        value={filtros.idCliente}
                        onChange={handleChange}
                        disabled={!filtros.idUsina}
                    >
                        <option value="">Todos os Clientes</option>
                        {clientes.map(c =>
                            <option key={c.id} value={c.id}>{c.nome} - {c.unidadeNome}</option>)}
                    </select>
                </div>

                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>
                </div>
            </form>

            {loading && <p className={styles_global.loading}>Carregando...</p>}

            {/* === botões de ação ================================================= */}
            {!loading && lista.length > 0 && (
                <div className={styles.btnToolbar}>
                    <BotaoFaturamento
                        filtros={filtros}
                        idsConsumo={lista.map(l => l.idConsumoCliente)}          // ou passe array se precisar
                        className={styles.btnAcao}>
                        <FaReceipt/> Relatório Faturamento (PDF)
                    </BotaoFaturamento>

                    <BotaoEmailContabilidade
                        filtros={filtros}
                        idsConsumo={lista.map(l => l.idConsumoCliente)}
                        className={styles.btnAcao}>
                        <FaPaperPlane/> Enviar para a Contabilidade
                    </BotaoEmailContabilidade>
                </div>
            )}

            {/* Tabela */}
            {!loading && lista.length > 0 && (
                <>
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                            <tr>
                                <th>USINA</th>
                                <th>CLIENTE</th>
                                <th>UNIDADE</th>
                                <th>VALOR (R$)</th>
                            </tr>
                            </thead>
                            <tbody>
                            {lista.map(faturamento  => (
                                <tr key={faturamento.idConsumoCliente}>
                                    <td>{faturamento.empresaEmissora}</td>
                                    <td>{faturamento.empresaDest}</td>
                                    <td>{faturamento.unidadeEmpresaDest}</td>
                                    <td className={styles.valorCell}>
                                        {Number(faturamento.valor).toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                    </td>
                                </tr>
                            ))}
                            {/* totalizador */}
                            <tr className={styles.totalRow}>
                                <td colSpan={3}>TOTAL FATURAMENTO</td>
                                <td className={styles.valorCell}>
                                    {Number(total).toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </>
            )}

            {!loading && lista.length === 0 && initialized.current && (
                <p className={styles.noData}>Nenhum faturamento encontrado.</p>
            )}
        </div>
    );
}
